import { cloneDeep } from 'lodash';
import apiClient from '@/utils/apiClient';

const DRAFTS_ENDPOINT = '/api/price-list-drafts/';

/**
 * @param {string} priceListType
 * @param {number|string|null} priceListId
 * @returns {{ type: string, price_list_id: (number|string) }}
 */
function draftScopeParams(priceListType, priceListId) {
    return {
        type: priceListType || 'general',
        price_list_id: priceListId || 'none',
    };
}

/**
 * Loads all product drafts for a price list scope from the backend.
 * @param {string} priceListType
 * @param {number|string|null} priceListId
 * @returns {Promise<Array<Object>>}
 */
export async function fetchDraftQueue(priceListType, priceListId) {
    try {
        const response = await apiClient.get(DRAFTS_ENDPOINT, {
            params: draftScopeParams(priceListType, priceListId),
        });
        return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
        console.error('Failed to load price list drafts:', error);
        return [];
    }
}

/**
 * @param {unknown} id
 * @returns {boolean}
 */
export function isPersistedDraftId(id) {
    if (id == null || id === '') {
        return false;
    }
    if (typeof id === 'number') {
        return Number.isInteger(id) && id > 0;
    }
    const normalized = String(id).trim();
    return /^\d+$/.test(normalized);
}

/**
 * @param {unknown} id
 * @returns {number|null}
 */
export function normalizePersistedDraftId(id) {
    if (!isPersistedDraftId(id)) {
        return null;
    }
    return typeof id === 'number' ? id : Number(String(id).trim());
}

/**
 * @param {Object} entry
 * @param {string} priceListType
 * @param {number|string|null} priceListId
 * @returns {Object}
 */
function buildDraftRequestBody(entry, priceListType, priceListId) {
    const body = {
        ...draftScopeParams(priceListType, priceListId),
        action: entry.action,
        updateMode: entry.updateMode,
        payload: entry.payload,
        builderState: entry.builderState,
        summary: entry.summary,
        sourceProductCode: entry.sourceProductCode,
        previousPrice: entry.previousPrice,
    };
    const persistedId = normalizePersistedDraftId(entry?.id);
    if (persistedId != null) {
        body.id = persistedId;
    }
    return body;
}

/**
 * Creates or updates a single draft entry on the backend.
 * @param {Object} params
 * @param {string} params.priceListType
 * @param {number|string|null} params.priceListId
 * @param {Object} params.entry - Draft entry (see createDraftEntry).
 * @returns {Promise<Object>} The persisted draft entry (with server id).
 */
export async function saveDraftEntry({ priceListType, priceListId, entry }) {
    const body = buildDraftRequestBody(entry, priceListType, priceListId);
    const response = await apiClient.post(DRAFTS_ENDPOINT, body);
    return response.data;
}

const BULK_DRAFTS_ENDPOINT = `${DRAFTS_ENDPOINT}bulk/`;
const PUBLISH_DRAFTS_ENDPOINT = `${DRAFTS_ENDPOINT}publish/`;
const DEFAULT_BULK_CHUNK_SIZE = 60;
const DEFAULT_PUBLISH_CHUNK_SIZE = 20;

/**
 * Upserts many drafts in chunked bulk requests (avoids one-by-one HTTP latency).
 * @param {Object} params
 * @param {string} params.priceListType
 * @param {number|string|null} params.priceListId
 * @param {Array<Object>} params.entries
 * @param {number} [params.chunkSize]
 * @param {(saved: number, total: number) => void} [params.onProgress]
 * @returns {Promise<{ saved: Object[], failedCodes: string[] }>}
 */
export async function saveDraftEntriesBulk({
    priceListType,
    priceListId,
    entries,
    chunkSize = DEFAULT_BULK_CHUNK_SIZE,
    onProgress,
}) {
    const list = Array.isArray(entries) ? entries : [];
    if (!list.length) {
        return { saved: [], failedCodes: [] };
    }
    const size = Math.max(1, Math.min(80, Number(chunkSize) || DEFAULT_BULK_CHUNK_SIZE));
    const saved = [];
    const failedCodes = [];

    for (let offset = 0; offset < list.length; offset += size) {
        const chunk = list.slice(offset, offset + size);
        const drafts = chunk.map((entry) =>
            buildDraftRequestBody(entry, priceListType, priceListId),
        );
        try {
            const response = await apiClient.post(BULK_DRAFTS_ENDPOINT, {
                ...draftScopeParams(priceListType, priceListId),
                drafts,
            });
            const batch = Array.isArray(response.data?.drafts)
                ? response.data.drafts
                : [];
            saved.push(...batch);
            const errors = Array.isArray(response.data?.errors)
                ? response.data.errors
                : [];
            errors.forEach((err) => {
                failedCodes.push(String(err?.product_code || 'necunoscut').trim() || 'necunoscut');
            });
        } catch (error) {
            console.error('Failed to save draft bulk chunk:', error);
            chunk.forEach((entry) => {
                failedCodes.push(
                    String(entry?.payload?.product_code || 'necunoscut').trim() || 'necunoscut',
                );
            });
        }
        if (typeof onProgress === 'function') {
            onProgress(Math.min(offset + chunk.length, list.length), list.length);
        }
    }

    return { saved, failedCodes };
}

/**
 * Publishes many drafts into a target price list via chunked bulk requests.
 * Successful drafts are deleted server-side.
 * @param {Object} params
 * @param {number|string} params.priceListId
 * @param {Array<number|string>} params.draftIds - Persisted draft ids
 * @param {number} [params.chunkSize]
 * @param {(done: number, total: number) => void} [params.onProgress]
 * @returns {Promise<{
 *   succeeded: Array<{draft_id: number, product_code: string}>,
 *   failed: Array<{draft_id: number, product_code: string, error: string}>,
 * }>}
 */
export async function publishDraftEntriesBulk({
    priceListId,
    draftIds,
    chunkSize = DEFAULT_PUBLISH_CHUNK_SIZE,
    onProgress,
}) {
    const ids = (Array.isArray(draftIds) ? draftIds : [])
        .map((id) => normalizePersistedDraftId(id))
        .filter((id) => id != null);
    if (!ids.length) {
        return { succeeded: [], failed: [] };
    }
    const size = Math.max(
        1,
        Math.min(25, Number(chunkSize) || DEFAULT_PUBLISH_CHUNK_SIZE),
    );
    const succeeded = [];
    const failed = [];

    for (let offset = 0; offset < ids.length; offset += size) {
        const chunk = ids.slice(offset, offset + size);
        try {
            const response = await apiClient.post(PUBLISH_DRAFTS_ENDPOINT, {
                price_list_id: priceListId,
                draft_ids: chunk,
            });
            const batchSucceeded = Array.isArray(response.data?.succeeded)
                ? response.data.succeeded
                : [];
            const batchFailed = Array.isArray(response.data?.failed)
                ? response.data.failed
                : [];
            succeeded.push(...batchSucceeded);
            failed.push(...batchFailed);
        } catch (error) {
            console.error('Failed to publish draft bulk chunk:', error);
            const message =
                error?.response?.data?.error
                || error?.response?.data?.detail
                || 'Cererea de publicare a eșuat.';
            chunk.forEach((draftId) => {
                failed.push({
                    draft_id: draftId,
                    product_code: '',
                    error: message,
                });
            });
        }
        if (typeof onProgress === 'function') {
            onProgress(Math.min(offset + chunk.length, ids.length), ids.length);
        }
    }

    return { succeeded, failed };
}

/**
 * Resolves the backend draft id for a queue entry (handles client UUID ids).
 * @param {Object} draft
 * @param {string} priceListType
 * @param {number|string|null} priceListId
 * @returns {Promise<number|null>}
 */
export async function resolvePersistedDraftId(draft, priceListType, priceListId) {
    const direct = normalizePersistedDraftId(draft?.id);
    if (direct != null) {
        return direct;
    }
    const productCode = String(draft.payload?.product_code || '').trim();
    if (!productCode) {
        return null;
    }
    const drafts = await fetchDraftQueue(priceListType, priceListId);
    const match = drafts.find(
        (row) => String(row.payload?.product_code || '').trim() === productCode,
    );
    return normalizePersistedDraftId(match?.id);
}

/**
 * Moves a draft from one price list scope to another.
 * @param {Object} params
 * @param {string} params.priceListType
 * @param {number|string|null} [params.fromPriceListId]
 * @param {number|string} params.toPriceListId
 * @param {Object} params.entry
 * @returns {Promise<Object>}
 */
export async function migrateDraftEntry({
    priceListType,
    fromPriceListId = null,
    toPriceListId,
    entry,
}) {
    const persistedId = normalizePersistedDraftId(entry?.id);
    if (persistedId != null) {
        return saveDraftEntry({
            priceListType,
            priceListId: toPriceListId,
            entry: { ...entry, id: persistedId },
        });
    }

    const productCode = String(entry.payload?.product_code || '').trim();
    const created = await saveDraftEntry({
        priceListType,
        priceListId: toPriceListId,
        entry,
    });

    if (productCode && fromPriceListId != null && fromPriceListId !== '') {
        const oldDrafts = await fetchDraftQueue(priceListType, fromPriceListId);
        const oldMatch = oldDrafts.find(
            (draft) => String(draft.payload?.product_code || '').trim() === productCode,
        );
        const oldId = normalizePersistedDraftId(oldMatch?.id);
        if (oldId != null) {
            try {
                await deleteDraftEntry(oldId);
            } catch (error) {
                console.error(
                    'Failed to remove migrated draft from previous scope:',
                    error,
                );
            }
        }
    }

    return created;
}

/**
 * Deletes a single draft entry from the backend.
 * @param {number|string} draftId
 * @returns {Promise<void>}
 */
export async function deleteDraftEntry(draftId) {
    const persistedId = normalizePersistedDraftId(draftId);
    if (persistedId == null) {
        return;
    }
    await apiClient.delete(`${DRAFTS_ENDPOINT}${persistedId}/`);
}

/**
 * Clears all drafts for a price list scope on the backend.
 * @param {string} priceListType
 * @param {number|string|null} priceListId
 * @returns {Promise<void>}
 */
export async function clearDraftQueue(priceListType, priceListId) {
    await apiClient.delete(DRAFTS_ENDPOINT, {
        params: draftScopeParams(priceListType, priceListId),
    });
}

/**
 * @returns {string}
 */
function createDraftId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `draft_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * @param {Object} payload
 * @param {string[]} existingProductCodes
 * @returns {'create'|'update'}
 */
export function resolveDraftAction(payload, existingProductCodes = []) {
    if (payload?.old_product_code) {
        return 'update';
    }
    const code = String(payload?.product_code || '').trim();
    if (code && existingProductCodes.includes(code)) {
        return 'update';
    }
    return 'create';
}

/**
 * @param {Object} params
 * @param {'create'|'update'} params.action
 * @param {Object} params.payload
 * @param {Object} [params.builderState]
 * @param {string|null} [params.sourceProductCode]
 * @param {number|string|null} [params.previousPrice]
 * @param {'simple'|'builder'} [params.updateMode]
 * @returns {Object}
 */
export function createDraftEntry({
    action,
    payload,
    builderState = null,
    sourceProductCode = null,
    previousPrice = null,
    updateMode = builderState ? 'builder' : 'simple',
}) {
    const normalizedPayload = cloneDeep(payload);
    if (action === 'update' && !normalizedPayload.old_product_code) {
        normalizedPayload.old_product_code = sourceProductCode || normalizedPayload.product_code;
    }

    // Never attach pricing_detail on simple price-only updates.
    if (updateMode === 'simple') {
        delete normalizedPayload.pricing_detail;
        delete normalizedPayload.pricing_settings;
    }

    return {
        id: createDraftId(),
        action,
        updateMode,
        payload: normalizedPayload,
        builderState: builderState ? cloneDeep(builderState) : null,
        sourceProductCode: sourceProductCode || normalizedPayload.old_product_code || null,
        previousPrice,
        createdAt: new Date().toISOString(),
        summary: {
            product_code: normalizedPayload.product_code,
            product_name: normalizedPayload.product_name,
            price: normalizedPayload.price,
            previous_price: previousPrice,
            action,
            update_mode: updateMode,
        },
    };
}

/**
 * @param {Array<Object>} drafts
 * @param {Object} entry
 * @param {string|null} [replaceDraftId]
 * @returns {Array<Object>}
 */
export function upsertDraftEntry(drafts, entry, replaceDraftId = null) {
    const queue = Array.isArray(drafts) ? [...drafts] : [];
    if (replaceDraftId) {
        const index = queue.findIndex((draft) => draft.id === replaceDraftId);
        if (index !== -1) {
            queue[index] = { ...entry, id: replaceDraftId };
            return queue;
        }
    }
    queue.push(entry);
    return queue;
}

/**
 * @param {Array<Object>} drafts
 * @param {string} draftId
 * @returns {Array<Object>}
 */
export function removeDraftEntry(drafts, draftId) {
    return (drafts || []).filter((draft) => draft.id !== draftId);
}

/**
 * @param {Object|null|undefined} draft
 * @returns {boolean}
 */
export function isExchangeRateRecalcDraft(draft) {
    return Boolean(draft?.builderState?.exchangeRateRecalc);
}

/**
 * True when lista în lucru contains drafts from a list exchange-rate recalc.
 * Those FX drafts cannot be deleted one-by-one; other drafts still can.
 * @param {Array<Object>|null|undefined} drafts
 * @returns {boolean}
 */
export function draftsHaveExchangeRateRecalcLock(drafts) {
    return (Array.isArray(drafts) ? drafts : []).some(isExchangeRateRecalcDraft);
}

/**
 * @param {Array<Object>|null|undefined} drafts
 * @returns {Object[]}
 */
export function filterExchangeRateRecalcDrafts(drafts) {
    return (Array.isArray(drafts) ? drafts : []).filter(isExchangeRateRecalcDraft);
}

/**
 * @param {Array<Object>|null|undefined} drafts
 * @returns {Object[]}
 */
export function filterNonExchangeRateRecalcDrafts(drafts) {
    return (Array.isArray(drafts) ? drafts : []).filter(
        (draft) => !isExchangeRateRecalcDraft(draft),
    );
}

/**
 * Previous list FX rates stamped when exchange-rate recalc drafts were created.
 * @param {Array<Object>|null|undefined} drafts
 * @returns {{
 *   exchangeRateEur: unknown,
 *   exchangeRatePln: unknown,
 *   exchangeRateUsd: unknown,
 * }|null}
 */
export function resolveExchangeRateRecalcPrevious(drafts) {
    for (const draft of (Array.isArray(drafts) ? drafts : [])) {
        const prev = draft?.builderState?.exchangeRateRecalcPrevious;
        if (!prev || typeof prev !== 'object') {
            continue;
        }
        if (
            prev.exchangeRateEur == null
            && prev.exchangeRatePln == null
            && prev.exchangeRateUsd == null
        ) {
            continue;
        }
        return {
            exchangeRateEur: prev.exchangeRateEur,
            exchangeRatePln: prev.exchangeRatePln,
            exchangeRateUsd: prev.exchangeRateUsd,
        };
    }
    return null;
}

/**
 * Keep FX-recalc lock markers when the builder rewrites builderState on draft save.
 * @param {Object|null|undefined} nextBuilderState
 * @param {Object|null|undefined} previousBuilderState
 * @returns {Object|null|undefined}
 */
export function preserveExchangeRateRecalcOnBuilderState(
    nextBuilderState,
    previousBuilderState,
) {
    if (!nextBuilderState || !previousBuilderState?.exchangeRateRecalc) {
        return nextBuilderState;
    }
    return {
        ...nextBuilderState,
        exchangeRateRecalc: true,
        exchangeRateRecalcPrevious:
            previousBuilderState.exchangeRateRecalcPrevious || null,
    };
}

/**
 * FX-recalc drafts must be applied as a full wave (all or none).
 * Product-only selection (zero FX) is allowed; partial FX selection is not.
 * @param {{
 *   drafts?: Array<Object>|null,
 *   selectedIds?: Array<string|number>|null,
 * }} params
 * @returns {{
 *   ok: boolean,
 *   includesFullFxWave: boolean,
 *   selectedFxCount: number,
 *   totalFxCount: number,
 * }}
 */
export function resolveExchangeRatePublishSelection({
    drafts = null,
    selectedIds = null,
} = {}) {
    const allDrafts = Array.isArray(drafts) ? drafts : [];
    const fxDrafts = filterExchangeRateRecalcDrafts(allDrafts);
    const totalFxCount = fxDrafts.length;
    if (!totalFxCount) {
        return {
            ok: true,
            includesFullFxWave: false,
            selectedFxCount: 0,
            totalFxCount: 0,
        };
    }

    const selectedList = Array.isArray(selectedIds) ? selectedIds : [];
    const selectedSet = selectedList.length
        ? new Set(selectedList.map((id) => String(id)))
        : new Set(
            allDrafts
                .map((draft) => draft?.id)
                .filter((id) => id != null && id !== '')
                .map((id) => String(id)),
        );

    const selectedFxCount = fxDrafts.filter((draft) => (
        selectedSet.has(String(draft.id))
    )).length;

    if (selectedFxCount === 0) {
        return {
            ok: true,
            includesFullFxWave: false,
            selectedFxCount: 0,
            totalFxCount,
        };
    }
    if (selectedFxCount === totalFxCount) {
        return {
            ok: true,
            includesFullFxWave: true,
            selectedFxCount,
            totalFxCount,
        };
    }
    return {
        ok: false,
        includesFullFxWave: false,
        selectedFxCount,
        totalFxCount,
    };
}

/**
 * Whether Aplică should promote header FX into appliedExchangeRates.
 * Product-only publishes must not stamp pending curs as applied; commit only
 * when the full FX-recalc wave was published and none remain pending.
 * @param {{
 *   publishedDrafts?: Array<Object>|null,
 *   remainingDrafts?: Array<Object>|null,
 * }} params
 * @returns {boolean}
 */
export function shouldCommitAppliedListExchangeRates({
    publishedDrafts = null,
    remainingDrafts = null,
} = {}) {
    if (!draftsHaveExchangeRateRecalcLock(publishedDrafts)) {
        return false;
    }
    return !draftsHaveExchangeRateRecalcLock(remainingDrafts);
}

/**
 * @param {Array<Object>} drafts
 * @param {string|null} [excludeDraftId]
 * @returns {string[]}
 */
export function getDraftProductCodes(drafts, excludeDraftId = null) {
    return (drafts || [])
        .filter((draft) => draft.id !== excludeDraftId)
        .map((draft) => String(draft.payload?.product_code || '').trim())
        .filter(Boolean);
}

/**
 * @param {string} productCode
 * @param {Array<Object>} drafts
 * @param {string|null} [excludeDraftId]
 * @returns {Object|null}
 */
export function findDraftByProductCode(drafts, productCode, excludeDraftId = null) {
    const normalized = String(productCode || '').trim();
    if (!normalized) {
        return null;
    }
    return (drafts || []).find((draft) =>
        draft.id !== excludeDraftId
        && String(draft.payload?.product_code || '').trim() === normalized
    ) || null;
}
